import PropTypes from 'prop-types';
import { createContext, useContext, useMemo, useReducer } from 'react';

export const PAGE_SIZE = 100;

const initialState = {
  summaryRefreshKey: 0,
  summary: {
    loading: false,
    lowStock: { total: null, criticalCount: null, lowStockCount: null },
    expiringSoon: { total: null, withinDays: 30 },
    lowDaysOfSupply: { total: null, thresholdDays: 14 },
    inPhysicalCount: { lockedItems: null, totalInventoryItems: null },
  },
  physicalCount: {
    items: [],
    loading: false,
    loadedForKey: null,
  },
  alertDetails: {
    active: null,
    facilityId: null,
    lowStock: { totalCount: null, pages: {}, rowsLoading: false },
    expiringSoon: { totalCount: null, pages: {}, rowsLoading: false },
    lowDaysOfSupply: { totalCount: null, pages: {}, rowsLoading: false },
    inPhysicalCount: { totalCount: null, pages: {}, rowsLoading: false },
  },
};

function reducer(state, action) {
  switch (action.type) {
    case 'SUMMARY_LOADING':
      return { ...state, summary: { ...state.summary, loading: action.loading } };

    case 'SUMMARY_LOADED':
      return {
        ...state,
        summary: {
          loading: false,
          lowStock: action.payload.lowStock,
          expiringSoon: action.payload.expiringSoon,
          lowDaysOfSupply: action.payload.lowDaysOfSupply,
          inPhysicalCount: action.payload.inPhysicalCount,
        },
      };

    case 'REFRESH_SUMMARY':
      return { ...state, summaryRefreshKey: state.summaryRefreshKey + 1 };

    case 'PHYSICAL_COUNT_CLEAR':
      return { ...state, physicalCount: { items: [], loading: false, loadedForKey: null } };

    case 'PHYSICAL_COUNT_LOADING':
      return { ...state, physicalCount: { ...state.physicalCount, loading: true } };

    case 'PHYSICAL_COUNT_LOADED':
      return {
        ...state,
        physicalCount: { items: action.items, loading: false, loadedForKey: action.loadedForKey },
      };

    case 'OPEN_DIALOG':
      return {
        ...state,
        alertDetails: {
          ...state.alertDetails,
          active: action.dialogType,
          facilityId: action.facilityId ?? null,
        },
      };

    case 'CLOSE_DIALOG': {
      const { active } = state.alertDetails;
      return {
        ...state,
        alertDetails: {
          ...state.alertDetails,
          active: null,
          facilityId: null,
          ...(active ? { [active]: { totalCount: null, pages: {}, rowsLoading: false } } : {}),
        },
      };
    }

    case 'ROWS_LOADING':
      return {
        ...state,
        alertDetails: {
          ...state.alertDetails,
          [action.dialogType]: {
            ...state.alertDetails[action.dialogType],
            rowsLoading: action.loading,
          },
        },
      };

    case 'ROWS_LOADED': {
      const { dialogType, page, rows, totalCount } = action;
      // For standard dialog types, correct card count if list API reports a different total
      const currentSummaryTotal = state.summary[dialogType]?.total;
      const updatedSummary =
        currentSummaryTotal != null && currentSummaryTotal !== totalCount
          ? { ...state.summary, [dialogType]: { ...state.summary[dialogType], total: totalCount } }
          : state.summary;

      return {
        ...state,
        summary: updatedSummary,
        alertDetails: {
          ...state.alertDetails,
          [dialogType]: {
            ...state.alertDetails[dialogType],
            rowsLoading: false,
            totalCount,
            pages: { ...state.alertDetails[dialogType].pages, [page]: rows },
          },
        },
      };
    }

    case 'PAGE_LOAD_FAILED': {
      const { dialogType, page } = action;
      return {
        ...state,
        alertDetails: {
          ...state.alertDetails,
          [dialogType]: {
            ...state.alertDetails[dialogType],
            rowsLoading: false,
            pages: { ...state.alertDetails[dialogType].pages, [page]: [] },
          },
        },
      };
    }

    case 'CLEAR_ALL_PAGES':
      return {
        ...state,
        alertDetails: {
          active: null,
          facilityId: null,
          lowStock: { totalCount: null, pages: {}, rowsLoading: false },
          expiringSoon: { totalCount: null, pages: {}, rowsLoading: false },
          lowDaysOfSupply: { totalCount: null, pages: {}, rowsLoading: false },
          inPhysicalCount: { totalCount: null, pages: {}, rowsLoading: false },
        },
      };

    default:
      return state;
  }
}

const InventoryAlertContext = createContext(null);

export const InventoryAlertProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const contextValue = useMemo(() => ({ state, dispatch }), [state, dispatch]);
  return (
    <InventoryAlertContext.Provider value={contextValue}>{children}</InventoryAlertContext.Provider>
  );
};

InventoryAlertProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useInventoryAlert = () => {
  const ctx = useContext(InventoryAlertContext);
  if (!ctx) throw new Error('useInventoryAlert must be used within InventoryAlertProvider');
  return ctx;
};
